import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { getToolkitIconByName } from "@arcadeai/design-system/metadata/toolkit-icons";
import type { ToolkitCollection } from "@arcadeai/ui-kit/arcade/arcade";
import { ToolkitPicker } from "@arcadeai/ui-kit/toolkit-picker";
import { ChevronDown, ExternalLink } from "lucide-react";
import { useMemo } from "react";
import type { Tool } from "@/app/tools/types";
import { useSearchEmptyTracking } from "@/hooks/use-search-empty-tracking";

type ToolSelectorProps = {
  selectedTool: Tool;
  toolkitCollection: ToolkitCollection | undefined;
  onSelectionChange: (selection: {
    selectedToolkits: string[];
    selectedTools: string[];
  }) => void;
};

export function ToolSelector({
  selectedTool,
  toolkitCollection,
  onSelectionChange,
}: ToolSelectorProps) {
  const handleSearchEmpty = useSearchEmptyTracking();
  const ToolkitIcon = getToolkitIconByName(selectedTool.toolkit?.name ?? "");
  const hasToolkitInfo = Boolean(selectedTool.toolkit?.name);
  const hasToolkitVersion = Boolean(selectedTool.toolkit?.version);

  const toolkitMetadata = useMemo(() => {
    if (!(selectedTool.toolkit?.name && toolkitCollection)) {
      return null;
    }
    const toolkit = toolkitCollection.toolkitMap.get(selectedTool.toolkit.name);
    return toolkit?.metadata;
  }, [selectedTool.toolkit?.name, toolkitCollection]);

  const docsLink = toolkitMetadata?.docsLink;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label
          className="font-medium text-muted-foreground text-sm"
          htmlFor="tool-selector-trigger"
        >
          Select Tool
        </label>
        {docsLink ? (
          <a
            className="flex items-center gap-1.5 text-muted-foreground text-xs transition-colors hover:text-foreground"
            href={docsLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span>Docs</span>
          </a>
        ) : null}
      </div>
      <ToolkitPicker
        initialProps={{
          selectedToolkits: selectedTool.toolkit?.name
            ? [selectedTool.toolkit.name]
            : [],
          selectedTools: [selectedTool.fully_qualified_name],
          committedToolkits: selectedTool.toolkit?.name
            ? [selectedTool.toolkit.name]
            : [],
          committedTools: [selectedTool.fully_qualified_name],
        }}
        onSearchEmpty={handleSearchEmpty}
        onSelectionChange={onSelectionChange}
        selectionMode="single"
        toolkitCollection={toolkitCollection}
      >
        <Button
          aria-label="Select tool"
          className="h-auto w-full justify-between gap-4 border-neutral-200/60 bg-neutral-100/50 px-4 py-3 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-200 dark:hover:bg-neutral-900"
          id="tool-selector-trigger"
          variant="outline"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-neutral-50 to-neutral-100 shadow-sm ring-1 ring-neutral-200/80 dark:from-neutral-800 dark:to-neutral-900 dark:ring-neutral-700/80">
              <ToolkitIcon className="h-6 w-6" />
            </div>
            <div className="flex flex-col items-start">
              <span className="truncate font-medium">{selectedTool.name}</span>
              {hasToolkitInfo ? (
                <span className="text-neutral-600 text-sm dark:text-neutral-400">
                  {selectedTool.toolkit?.name}
                  {hasToolkitVersion ? (
                    <span className="text-xs opacity-60">
                      {" "}
                      • {selectedTool.toolkit?.version}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
          </div>
          <ChevronDown className="size-5 shrink-0 text-neutral-500 dark:text-neutral-400" />
        </Button>
      </ToolkitPicker>
    </div>
  );
}
